import { CalendarDaysIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useState } from 'react';
import { FormDialog } from '~/components/dialogs/FormDialog';
import { UsStateSelect } from '~/components/forms/UsStateSelect';
import {
  CALENDAR_ICON_TEXT_BUTTON,
  CALENDAR_OUTLINE_BUTTON,
  CALENDAR_PRIMARY_BUTTON,
} from '~/features/shared/calendar/controlStyles';
import { addDays, parseISODate, toISODate } from '~/features/shared/calendar/dateUtils';
import { SHIFT_TEMPLATES, WEEKDAYS } from '../constants';
import type {
  AvailabilityConflict,
  AvailabilityRule,
  AvailabilityScope,
  ShiftLabel,
  ShiftType,
  WorkLocation,
  WritableAvailabilityException,
  WritableAvailabilityRule,
  WritableAvailabilityRulePatch,
} from '../types';
import { scopeServiceListingId } from '../types';

interface RuleForm {
  daysOfWeekMask: number;
  detailMode: 'DATE_ONLY' | 'SHIFT_DETAIL';
  effectiveEndDate: string;
  effectiveStartDate: string;
  endTime: string;
  notes: string;
  shiftLabel: ShiftLabel;
  shiftType: ShiftType;
  startTime: string;
  stateCode: string;
  workLocation: WorkLocation | '';
}

interface ExceptionForm {
  exceptionDate: string;
  exceptionType: 'CANCEL' | 'TIME_OVERRIDE' | 'SHIFT_OVERRIDE';
  notes: string;
  overrideEndTime: string;
  overrideShiftLabel: ShiftLabel;
  overrideShiftType: ShiftType;
  overrideStartTime: string;
  ruleId: number;
}

interface RecurringAvailabilityDialogProps {
  busy: boolean;
  conflict?: AvailabilityConflict | null;
  mutationError?: string | null;
  notice?: string | null;
  onClose: () => void;
  onDelete: (ruleId: number) => void;
  onSaveException: (ruleId: number, exception: WritableAvailabilityException) => void;
  onSave: (rule: WritableAvailabilityRule | WritableAvailabilityRulePatch, ruleId?: number) => void;
  open: boolean;
  readOnly: boolean;
  rules: AvailabilityRule[];
  scope: AvailabilityScope;
  timezone: string | null;
  todayISO: string;
}

const addCalendarYears = (isoDate: string, amount: number): string => {
  const date = parseISODate(isoDate);
  const targetYear = date.getFullYear() + amount;
  const result = new Date(targetYear, date.getMonth(), date.getDate());
  if (result.getMonth() !== date.getMonth()) {
    return toISODate(new Date(targetYear, date.getMonth() + 1, 0));
  }
  return toISODate(result);
};

const blankForm = (todayISO: string): RuleForm => ({
  daysOfWeekMask: 31,
  detailMode: 'DATE_ONLY',
  effectiveEndDate: '',
  effectiveStartDate: todayISO,
  endTime: SHIFT_TEMPLATES.DAY.endTime,
  notes: '',
  shiftLabel: 'DAY',
  shiftType: 'SHIFT',
  startTime: SHIFT_TEMPLATES.DAY.startTime,
  stateCode: '',
  workLocation: '',
});

const ruleToForm = (rule: AvailabilityRule): RuleForm => ({
  daysOfWeekMask: rule.daysOfWeekMask,
  detailMode: rule.detailMode,
  effectiveEndDate: rule.effectiveEndDate ?? '',
  effectiveStartDate: rule.effectiveStartDate,
  endTime: rule.endTime?.slice(0, 5) ?? SHIFT_TEMPLATES.DAY.endTime,
  notes: rule.notes ?? '',
  shiftLabel: rule.shiftLabel ?? 'DAY',
  shiftType: rule.shiftType ?? 'SHIFT',
  startTime: rule.startTime?.slice(0, 5) ?? SHIFT_TEMPLATES.DAY.startTime,
  stateCode: rule.stateCode ?? '',
  workLocation: rule.workLocation ?? '',
});

const blankExceptionForm = (rule: AvailabilityRule, todayISO: string): ExceptionForm => {
  let cursor = parseISODate(
    todayISO < rule.effectiveStartDate ? rule.effectiveStartDate : todayISO
  );
  const last = parseISODate(rule.effectiveEndDate ?? addCalendarYears(todayISO, 3));
  while (cursor <= last) {
    const mondayBit = (cursor.getDay() + 6) % 7;
    if (rule.daysOfWeekMask & (1 << mondayBit)) break;
    cursor = addDays(cursor, 1);
  }

  return {
    exceptionDate: toISODate(cursor),
    exceptionType: 'CANCEL',
    notes: '',
    overrideEndTime: rule.endTime?.slice(0, 5) ?? SHIFT_TEMPLATES.DAY.endTime,
    overrideShiftLabel: rule.shiftLabel ?? 'DAY',
    overrideShiftType: rule.shiftType ?? 'SHIFT',
    overrideStartTime: rule.startTime?.slice(0, 5) ?? SHIFT_TEMPLATES.DAY.startTime,
    ruleId: rule.id,
  };
};

const previewDates = (form: RuleForm, todayISO: string): string[] => {
  if (!form.effectiveStartDate || form.daysOfWeekMask === 0) return [];
  const dates: string[] = [];
  let cursor = parseISODate(
    form.effectiveStartDate < todayISO ? todayISO : form.effectiveStartDate
  );
  const hardStop = form.effectiveEndDate
    ? parseISODate(form.effectiveEndDate)
    : addDays(cursor, 60);
  for (let count = 0; count < 366 && cursor <= hardStop && dates.length < 5; count += 1) {
    const mondayBit = (cursor.getDay() + 6) % 7;
    if (form.daysOfWeekMask & (1 << mondayBit)) dates.push(toISODate(cursor));
    cursor = addDays(cursor, 1);
  }
  return dates;
};

const buildRuleCreate = (
  form: RuleForm,
  serviceListingId: number | null,
  timezone: string,
  requestTimezone = timezone
): WritableAvailabilityRule => {
  const detailed = form.detailMode === 'SHIFT_DETAIL';
  return {
    serviceListingId,
    effectiveStartDate: form.effectiveStartDate,
    effectiveEndDate: form.effectiveEndDate || null,
    detailMode: form.detailMode,
    timezone,
    requestTimezone,
    daysOfWeekMask: form.daysOfWeekMask,
    startTime: detailed ? `${form.startTime}:00` : null,
    endTime: detailed ? `${form.endTime}:00` : null,
    shiftLabel: detailed ? form.shiftLabel : null,
    shiftType: detailed ? form.shiftType : null,
    workLocation: form.workLocation || null,
    stateCode: form.stateCode.trim().toUpperCase() || null,
    rateOverrideType: null,
    pricePerHourOverride: null,
    pricePerShiftOverride: null,
    pricePerCallShiftOverride: null,
    notes: form.notes.trim() || null,
  };
};

/** Build a true PATCH so hidden metadata and unchanged historical dates survive an edit. */
const buildRulePatch = (
  form: RuleForm,
  rule: AvailabilityRule,
  requestTimezone: string
): WritableAvailabilityRulePatch => {
  const candidate = buildRuleCreate(form, rule.serviceListingId, rule.timezone, requestTimezone);
  const patch: WritableAvailabilityRulePatch = {
    timezone: rule.timezone,
    requestTimezone,
  };

  if (candidate.effectiveStartDate !== rule.effectiveStartDate) {
    patch.effectiveStartDate = candidate.effectiveStartDate;
  }
  if (candidate.effectiveEndDate !== rule.effectiveEndDate) {
    patch.effectiveEndDate = candidate.effectiveEndDate;
  }
  if (candidate.detailMode !== rule.detailMode) patch.detailMode = candidate.detailMode;
  if (candidate.daysOfWeekMask !== rule.daysOfWeekMask) {
    patch.daysOfWeekMask = candidate.daysOfWeekMask;
  }

  const originalStartTime = rule.startTime?.slice(0, 8) ?? null;
  const originalEndTime = rule.endTime?.slice(0, 8) ?? null;
  if (candidate.startTime !== originalStartTime) patch.startTime = candidate.startTime;
  if (candidate.endTime !== originalEndTime) patch.endTime = candidate.endTime;
  if (candidate.shiftLabel !== rule.shiftLabel) patch.shiftLabel = candidate.shiftLabel;
  if (candidate.shiftType !== rule.shiftType) patch.shiftType = candidate.shiftType;
  if (candidate.workLocation !== rule.workLocation) patch.workLocation = candidate.workLocation;
  if (candidate.stateCode !== rule.stateCode) patch.stateCode = candidate.stateCode;
  if (candidate.notes !== rule.notes) patch.notes = candidate.notes;

  return patch;
};

export const RecurringAvailabilityDialog = ({
  busy,
  conflict = null,
  mutationError = null,
  notice = null,
  onClose,
  onDelete,
  onSaveException,
  onSave,
  open,
  readOnly,
  rules,
  scope,
  timezone,
  todayISO,
}: RecurringAvailabilityDialogProps) => {
  const [form, setForm] = useState<RuleForm>(() => blankForm(todayISO));
  const [editingRuleId, setEditingRuleId] = useState<number | undefined>();
  const [exceptionForm, setExceptionForm] = useState<ExceptionForm | null>(null);
  const [exceptionSubmissionPending, setExceptionSubmissionPending] = useState(false);
  const [confirmDeleteRuleId, setConfirmDeleteRuleId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const preview = useMemo(() => previewDates(form, todayISO), [form, todayISO]);
  const calendarHorizon = useMemo(() => addCalendarYears(todayISO, 3), [todayISO]);
  const formDisabled = busy || readOnly;

  useEffect(() => {
    if (!open) return;
    setForm(blankForm(todayISO));
    setEditingRuleId(undefined);
    setExceptionForm(null);
    setExceptionSubmissionPending(false);
    setConfirmDeleteRuleId(null);
    setError(null);
  }, [open, todayISO]);

  useEffect(() => {
    if (!exceptionSubmissionPending || busy) return;
    if (notice === 'Exception saved.') {
      setExceptionForm(null);
      setExceptionSubmissionPending(false);
    } else if (mutationError || conflict) {
      // Keep the form and its values in place so the provider can correct or retry.
      setExceptionSubmissionPending(false);
    }
  }, [busy, conflict, exceptionSubmissionPending, mutationError, notice]);

  useEffect(() => {
    if (confirmDeleteRuleId != null && !rules.some((rule) => rule.id === confirmDeleteRuleId)) {
      setConfirmDeleteRuleId(null);
    }
  }, [confirmDeleteRuleId, rules]);

  const reset = () => {
    setForm(blankForm(todayISO));
    setEditingRuleId(undefined);
    setExceptionForm(null);
    setExceptionSubmissionPending(false);
    setError(null);
  };

  const submit = () => {
    setError(null);
    const editingRule = rules.find((rule) => rule.id === editingRuleId);
    if (readOnly) {
      setError('Choose General availability or a service listing before making changes.');
      return;
    }
    if (!timezone) {
      setError('Your browser timezone could not be determined.');
      return;
    }
    if (!form.effectiveStartDate || form.daysOfWeekMask === 0) {
      setError('Choose a start date and at least one weekday.');
      return;
    }
    if (form.effectiveEndDate && form.effectiveEndDate < form.effectiveStartDate) {
      setError('The end date must be on or after the start date.');
      return;
    }
    if (
      form.effectiveEndDate &&
      form.effectiveEndDate > calendarHorizon &&
      (!editingRule || form.effectiveEndDate !== editingRule.effectiveEndDate)
    ) {
      setError('The end date cannot exceed the three-year calendar horizon.');
      return;
    }
    if (form.detailMode === 'SHIFT_DETAIL' && (!form.startTime || !form.endTime)) {
      setError('Choose both a start and end time.');
      return;
    }
    const serviceListingId = editingRule
      ? editingRule.serviceListingId
      : scopeServiceListingId(scope);
    onSave(
      editingRule
        ? buildRulePatch(form, editingRule, timezone)
        : buildRuleCreate(form, serviceListingId ?? null, timezone),
      editingRuleId
    );
  };

  const submitException = () => {
    setError(null);
    if (!exceptionForm) return;
    if (readOnly) {
      setError('Choose General availability or a service listing before making changes.');
      return;
    }
    if (!timezone) {
      setError('Your browser timezone could not be determined.');
      return;
    }
    const rule = rules.find((candidate) => candidate.id === exceptionForm.ruleId);
    if (!rule) {
      setError('The recurring rule is no longer available.');
      return;
    }
    if (
      !exceptionForm.exceptionDate ||
      exceptionForm.exceptionDate < rule.effectiveStartDate ||
      (rule.effectiveEndDate && exceptionForm.exceptionDate > rule.effectiveEndDate)
    ) {
      setError('Choose a date within the recurring rule range.');
      return;
    }
    const exceptionDate = parseISODate(exceptionForm.exceptionDate);
    const mondayBit = (exceptionDate.getDay() + 6) % 7;
    if (!(rule.daysOfWeekMask & (1 << mondayBit))) {
      setError('Choose a date that occurs in the recurring rule.');
      return;
    }
    if (rule.detailMode !== 'SHIFT_DETAIL' && exceptionForm.exceptionType !== 'CANCEL') {
      setError('Date-only rules support cancellation exceptions only.');
      return;
    }
    if (
      exceptionForm.exceptionType === 'TIME_OVERRIDE' &&
      (!exceptionForm.overrideStartTime || !exceptionForm.overrideEndTime)
    ) {
      setError('Choose both override times.');
      return;
    }

    setExceptionSubmissionPending(true);
    onSaveException(exceptionForm.ruleId, {
      exceptionDate: exceptionForm.exceptionDate,
      exceptionType: exceptionForm.exceptionType,
      timezone: rule.timezone,
      requestTimezone: timezone,
      overrideStartTime:
        exceptionForm.exceptionType === 'TIME_OVERRIDE'
          ? `${exceptionForm.overrideStartTime}:00`
          : null,
      overrideEndTime:
        exceptionForm.exceptionType === 'TIME_OVERRIDE'
          ? `${exceptionForm.overrideEndTime}:00`
          : null,
      overrideShiftLabel:
        exceptionForm.exceptionType === 'SHIFT_OVERRIDE' ? exceptionForm.overrideShiftLabel : null,
      overrideShiftType:
        exceptionForm.exceptionType === 'SHIFT_OVERRIDE' ? exceptionForm.overrideShiftType : null,
      notes: exceptionForm.notes.trim() || null,
    });
  };

  return (
    <FormDialog editing={open} onClose={onClose} title="Recurring availability" width="4xl">
      <div
        className="grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(280px,0.8fr)]"
        data-cy="provider-calendar-recurrence-dialog"
      >
        <div className="space-y-4">
          {readOnly ? (
            <p className="rounded-md bg-warning-50 p-3 text-sm text-warning-950">
              All availability is a read-only overview. Choose General availability or a service
              listing to create or change recurring schedules and exceptions.
            </p>
          ) : null}
          <fieldset disabled={formDisabled}>
            <legend className="body-text-sb text-grey-100">Weekdays</legend>
            <div className="mt-2 flex flex-wrap gap-2">
              {WEEKDAYS.map(({ bit, label }) => {
                const selected = Boolean(form.daysOfWeekMask & (1 << bit));
                return (
                  <button
                    aria-pressed={selected}
                    className={selected ? CALENDAR_PRIMARY_BUTTON : CALENDAR_OUTLINE_BUTTON}
                    key={label}
                    onClick={() =>
                      setForm((current) => ({
                        ...current,
                        daysOfWeekMask: current.daysOfWeekMask ^ (1 << bit),
                      }))
                    }
                    type="button"
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm text-grey-400">
              Effective start
              <input
                className="mt-1 w-full rounded-md border border-grey-800 px-3 py-2"
                data-cy="provider-calendar-recurrence-start"
                disabled={formDisabled}
                max={calendarHorizon}
                min={todayISO}
                onChange={(event) => {
                  const effectiveStartDate = event.target.value;
                  setForm((current) => ({
                    ...current,
                    effectiveStartDate,
                    effectiveEndDate: !current.effectiveEndDate
                      ? ''
                      : current.effectiveEndDate < effectiveStartDate ||
                          current.effectiveEndDate > calendarHorizon
                        ? calendarHorizon
                        : current.effectiveEndDate,
                  }));
                }}
                type="date"
                value={form.effectiveStartDate}
              />
            </label>
            <label className="text-sm text-grey-400">
              Effective end
              <input
                className="mt-1 w-full rounded-md border border-grey-800 px-3 py-2"
                data-cy="provider-calendar-recurrence-end"
                disabled={formDisabled}
                max={calendarHorizon}
                min={form.effectiveStartDate}
                onChange={(event) =>
                  setForm((current) => ({ ...current, effectiveEndDate: event.target.value }))
                }
                type="date"
                value={form.effectiveEndDate}
              />
            </label>
          </div>

          <fieldset disabled={formDisabled}>
            <legend className="body-text-sb text-grey-100">Detail</legend>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {(['DATE_ONLY', 'SHIFT_DETAIL'] as const).map((mode) => (
                <button
                  aria-pressed={form.detailMode === mode}
                  className={
                    form.detailMode === mode ? CALENDAR_PRIMARY_BUTTON : CALENDAR_OUTLINE_BUTTON
                  }
                  key={mode}
                  onClick={() => setForm((current) => ({ ...current, detailMode: mode }))}
                  type="button"
                >
                  {mode === 'DATE_ONLY' ? 'Date only' : 'Shift details'}
                </button>
              ))}
            </div>
          </fieldset>

          {form.detailMode === 'SHIFT_DETAIL' ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm text-grey-400">
                Shift template
                <select
                  className="mt-1 w-full rounded-md border border-grey-800 px-3 py-2"
                  disabled={formDisabled}
                  onChange={(event) => {
                    const shiftLabel = event.target.value as ShiftLabel;
                    setForm((current) => ({
                      ...current,
                      shiftLabel,
                      ...SHIFT_TEMPLATES[shiftLabel],
                    }));
                  }}
                  value={form.shiftLabel}
                >
                  {(['DAY', 'EVENING', 'NIGHT', 'CUSTOM'] as ShiftLabel[]).map((label) => (
                    <option key={label} value={label}>
                      {label.charAt(0) + label.slice(1).toLowerCase()}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm text-grey-400">
                Shift type
                <select
                  className="mt-1 w-full rounded-md border border-grey-800 px-3 py-2"
                  disabled={formDisabled}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      shiftType: event.target.value as ShiftType,
                    }))
                  }
                  value={form.shiftType}
                >
                  <option value="SHIFT">Active shift</option>
                  <option value="CALL">On call</option>
                </select>
              </label>
              <label className="text-sm text-grey-400">
                Start time
                <input
                  className="mt-1 w-full rounded-md border border-grey-800 px-3 py-2"
                  disabled={formDisabled}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, startTime: event.target.value }))
                  }
                  type="time"
                  value={form.startTime}
                />
              </label>
              <label className="text-sm text-grey-400">
                End time
                <input
                  className="mt-1 w-full rounded-md border border-grey-800 px-3 py-2"
                  disabled={formDisabled}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, endTime: event.target.value }))
                  }
                  type="time"
                  value={form.endTime}
                />
              </label>
            </div>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm text-grey-400">
              Work location
              <select
                className="mt-1 w-full rounded-md border border-grey-800 bg-white px-3 py-2 text-grey-100"
                data-cy="provider-calendar-recurrence-work-location"
                disabled={formDisabled}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    workLocation: event.target.value as WorkLocation | '',
                  }))
                }
                value={form.workLocation}
              >
                <option value="">Not specified</option>
                <option value="ONSITE">Onsite</option>
                <option value="PARTIAL_REMOTE">Hybrid</option>
                <option value="REMOTE">Remote / telemedicine</option>
              </select>
            </label>
            <label className="text-sm text-grey-400">
              State
              <UsStateSelect
                className="mt-1 w-full rounded-md border border-grey-800 bg-white px-3 py-2 text-grey-100"
                data-cy="provider-calendar-recurrence-state"
                disabled={formDisabled}
                onChange={(stateCode) => setForm((current) => ({ ...current, stateCode }))}
                value={form.stateCode}
              />
            </label>
          </div>

          <label className="block text-sm text-grey-400">
            Private note
            <textarea
              className="mt-1 min-h-20 w-full rounded-md border border-grey-800 px-3 py-2"
              disabled={formDisabled}
              onChange={(event) =>
                setForm((current) => ({ ...current, notes: event.target.value }))
              }
              value={form.notes}
            />
          </label>

          <div className="rounded-md bg-grey-950 p-3">
            <p className="text-xs font-semibold uppercase text-grey-500">Next dates</p>
            <p className="mt-1 text-sm text-grey-100">
              {preview.length ? preview.join(', ') : 'Choose weekdays to preview occurrences.'}
            </p>
          </div>

          {error ? (
            <p className="text-sm text-error" aria-live="assertive">
              {error}
            </p>
          ) : null}
          {!error && (conflict || mutationError) ? (
            <p className="text-sm text-error" aria-live="assertive" role="alert">
              {conflict
                ? `${conflict.message} Locked entries: ${conflict.entryIds.join(', ') || 'unknown'}.`
                : mutationError}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            {editingRuleId != null ? (
              <button
                className={CALENDAR_OUTLINE_BUTTON}
                disabled={busy}
                onClick={reset}
                type="button"
              >
                Cancel edit
              </button>
            ) : null}
            <button
              className={CALENDAR_PRIMARY_BUTTON}
              data-cy="provider-calendar-recurrence-save"
              disabled={formDisabled}
              onClick={submit}
              type="button"
            >
              {busy ? 'Saving…' : editingRuleId == null ? 'Create rule' : 'Update rule'}
            </button>
          </div>
        </div>

        <section className="rounded-lg border border-grey-900 p-4">
          <h3 className="body-text-sb text-grey-100">Existing rules</h3>
          <div className="mt-3 space-y-3">
            {rules.length === 0 ? (
              <p className="text-sm text-grey-500">No recurring availability yet.</p>
            ) : null}
            {rules.map((rule) => (
              <article
                className="rounded-md bg-grey-950 p-3"
                data-cy={`provider-calendar-rule-${rule.id}`}
                key={rule.id}
              >
                <p className="body-text-sb text-grey-100">
                  {WEEKDAYS.filter(({ bit }) => rule.daysOfWeekMask & (1 << bit))
                    .map(({ label }) => label)
                    .join(', ')}
                </p>
                <p className="mt-1 text-xs text-grey-500">
                  {rule.detailMode === 'DATE_ONLY'
                    ? 'Date only'
                    : `${rule.shiftLabel} · ${rule.shiftType} · ${rule.startTime?.slice(0, 5)}–${rule.endTime?.slice(0, 5)}`}
                </p>
                <p className="mt-1 text-xs text-grey-500">
                  {rule.effectiveStartDate} → {rule.effectiveEndDate ?? 'Ongoing'}
                </p>
                <div className="mt-2 flex gap-2">
                  <button
                    aria-label={`Edit this and future dates for recurring rule ${rule.id}`}
                    className={CALENDAR_ICON_TEXT_BUTTON}
                    disabled={formDisabled}
                    onClick={() => {
                      setEditingRuleId(rule.id);
                      setForm(ruleToForm(rule));
                      setError(null);
                    }}
                    type="button"
                  >
                    <PencilSquareIcon className="size-4 shrink-0" />
                    <span>Edit this and future dates</span>
                  </button>
                  <button
                    aria-label={`Edit this date for recurring rule ${rule.id}`}
                    className={CALENDAR_ICON_TEXT_BUTTON}
                    data-cy={`provider-calendar-rule-exception-${rule.id}`}
                    disabled={formDisabled}
                    onClick={() => {
                      setExceptionForm(blankExceptionForm(rule, todayISO));
                      setError(null);
                    }}
                    type="button"
                  >
                    <CalendarDaysIcon className="size-4 shrink-0" />
                    <span>Edit this date</span>
                  </button>
                  <button
                    aria-label={`Delete recurring rule ${rule.id}`}
                    className="rounded p-1.5 text-error hover:bg-white"
                    data-cy={`provider-calendar-rule-delete-${rule.id}`}
                    disabled={formDisabled}
                    onClick={() => setConfirmDeleteRuleId(rule.id)}
                    type="button"
                  >
                    <TrashIcon className="size-4" />
                  </button>
                </div>

                {confirmDeleteRuleId === rule.id ? (
                  <div
                    className="mt-3 rounded-md border border-error/20 bg-white p-3"
                    data-cy={`provider-calendar-rule-delete-confirm-${rule.id}`}
                    role="alert"
                  >
                    <p className="text-sm text-grey-100">
                      Delete this rule and all of its editable future occurrences? This cannot be
                      undone.
                    </p>
                    <div className="mt-2 flex justify-end gap-2">
                      <button
                        className={CALENDAR_OUTLINE_BUTTON}
                        disabled={busy}
                        onClick={() => setConfirmDeleteRuleId(null)}
                        type="button"
                      >
                        Keep rule
                      </button>
                      <button
                        className={CALENDAR_PRIMARY_BUTTON}
                        data-cy={`provider-calendar-rule-delete-confirm-action-${rule.id}`}
                        disabled={busy}
                        onClick={() => onDelete(rule.id)}
                        type="button"
                      >
                        Delete rule
                      </button>
                    </div>
                  </div>
                ) : null}

                {exceptionForm?.ruleId === rule.id ? (
                  <div
                    className="mt-3 space-y-3 border-t border-grey-900 pt-3"
                    data-cy="provider-calendar-exception-form"
                  >
                    <p className="text-sm font-semibold text-grey-100">Single-date exception</p>
                    <label className="block text-xs text-grey-500">
                      Date
                      <input
                        className="mt-1 w-full rounded-md border border-grey-800 bg-white px-2 py-1.5 text-sm text-grey-100"
                        data-cy="provider-calendar-exception-date"
                        disabled={formDisabled}
                        max={rule.effectiveEndDate ?? calendarHorizon}
                        min={
                          rule.effectiveStartDate > todayISO ? rule.effectiveStartDate : todayISO
                        }
                        onChange={(event) =>
                          setExceptionForm((current) =>
                            current ? { ...current, exceptionDate: event.target.value } : current
                          )
                        }
                        type="date"
                        value={exceptionForm.exceptionDate}
                      />
                    </label>
                    <label className="block text-xs text-grey-500">
                      Change
                      <select
                        className="mt-1 w-full rounded-md border border-grey-800 bg-white px-2 py-1.5 text-sm text-grey-100"
                        data-cy="provider-calendar-exception-type"
                        disabled={formDisabled}
                        onChange={(event) =>
                          setExceptionForm((current) =>
                            current
                              ? {
                                  ...current,
                                  exceptionType: event.target
                                    .value as ExceptionForm['exceptionType'],
                                }
                              : current
                          )
                        }
                        value={exceptionForm.exceptionType}
                      >
                        <option value="CANCEL">Cancel this date</option>
                        {rule.detailMode === 'SHIFT_DETAIL' ? (
                          <>
                            <option value="TIME_OVERRIDE">Change times</option>
                            <option value="SHIFT_OVERRIDE">Change shift</option>
                          </>
                        ) : null}
                      </select>
                    </label>

                    {exceptionForm.exceptionType === 'TIME_OVERRIDE' ? (
                      <div className="grid grid-cols-2 gap-2">
                        <label className="text-xs text-grey-500">
                          Start
                          <input
                            className="mt-1 w-full rounded-md border border-grey-800 bg-white px-2 py-1.5 text-sm text-grey-100"
                            disabled={formDisabled}
                            onChange={(event) =>
                              setExceptionForm((current) =>
                                current
                                  ? { ...current, overrideStartTime: event.target.value }
                                  : current
                              )
                            }
                            type="time"
                            value={exceptionForm.overrideStartTime}
                          />
                        </label>
                        <label className="text-xs text-grey-500">
                          End
                          <input
                            className="mt-1 w-full rounded-md border border-grey-800 bg-white px-2 py-1.5 text-sm text-grey-100"
                            disabled={formDisabled}
                            onChange={(event) =>
                              setExceptionForm((current) =>
                                current
                                  ? { ...current, overrideEndTime: event.target.value }
                                  : current
                              )
                            }
                            type="time"
                            value={exceptionForm.overrideEndTime}
                          />
                        </label>
                      </div>
                    ) : null}

                    {exceptionForm.exceptionType === 'SHIFT_OVERRIDE' ? (
                      <div className="grid grid-cols-2 gap-2">
                        <label className="text-xs text-grey-500">
                          Shift
                          <select
                            className="mt-1 w-full rounded-md border border-grey-800 bg-white px-2 py-1.5 text-sm text-grey-100"
                            disabled={formDisabled}
                            onChange={(event) =>
                              setExceptionForm((current) =>
                                current
                                  ? {
                                      ...current,
                                      overrideShiftLabel: event.target.value as ShiftLabel,
                                    }
                                  : current
                              )
                            }
                            value={exceptionForm.overrideShiftLabel}
                          >
                            {(['DAY', 'EVENING', 'NIGHT', 'CUSTOM'] as ShiftLabel[]).map(
                              (label) => (
                                <option key={label} value={label}>
                                  {label.charAt(0) + label.slice(1).toLowerCase()}
                                </option>
                              )
                            )}
                          </select>
                        </label>
                        <label className="text-xs text-grey-500">
                          Type
                          <select
                            className="mt-1 w-full rounded-md border border-grey-800 bg-white px-2 py-1.5 text-sm text-grey-100"
                            disabled={formDisabled}
                            onChange={(event) =>
                              setExceptionForm((current) =>
                                current
                                  ? {
                                      ...current,
                                      overrideShiftType: event.target.value as ShiftType,
                                    }
                                  : current
                              )
                            }
                            value={exceptionForm.overrideShiftType}
                          >
                            <option value="SHIFT">Active shift</option>
                            <option value="CALL">On call</option>
                          </select>
                        </label>
                      </div>
                    ) : null}

                    <label className="block text-xs text-grey-500">
                      Note
                      <textarea
                        className="mt-1 min-h-16 w-full rounded-md border border-grey-800 bg-white px-2 py-1.5 text-sm text-grey-100"
                        disabled={formDisabled}
                        onChange={(event) =>
                          setExceptionForm((current) =>
                            current ? { ...current, notes: event.target.value } : current
                          )
                        }
                        value={exceptionForm.notes}
                      />
                    </label>
                    <div className="flex justify-end gap-2">
                      <button
                        className={CALENDAR_OUTLINE_BUTTON}
                        disabled={busy}
                        onClick={() => setExceptionForm(null)}
                        type="button"
                      >
                        Cancel
                      </button>
                      <button
                        className={CALENDAR_PRIMARY_BUTTON}
                        data-cy="provider-calendar-exception-save"
                        disabled={formDisabled}
                        onClick={submitException}
                        type="button"
                      >
                        Save exception
                      </button>
                    </div>
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      </div>
    </FormDialog>
  );
};
